import React, { useState, useEffect } from 'react';
import './LoadingOverlay.css';

/**
 * LoadingOverlay Component
 * 
 * Displays loading states with progressive status messages
 * during account creation, business setup, and login processes
 * 
 * Features:
 * - Sequential status messages
 * - Smooth transitions
 * - Pulsing animation
 * - Does not block carousel on left side
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isVisible - Show/hide overlay
 * @param {string} props.type - Type of loading: 'account' | 'business' | 'login' | 'custom'
 * @param {string[]} props.customMessages - Custom messages for 'custom' type
 */

// Predefined loading messages for different scenarios
const LOADING_MESSAGES = {
  // Public user popups (short messages)
  publicLogin: [
    'logging you in...',
    'almost there...',
  ],
  publicAccount: [
    'creating your account...',
    'setting up your profile...',
    'almost there...',
  ],
  // Business login/registration (full messages)
  login: [
    'logging you in...',
    'loading your dashboard...',
    'almost there...',
  ],
  account: [
    'creating your account...',
    'setting up your profile...',
    'preparing your workspace...',
  ],
  business: [
    'creating your business profile...',
    'setting up your business location...',
    'configuring your settings...',
  ],
  registration: [
    'creating your account...',
    'setting up your business profile...',
    'logging you in...',
  ]
};

const LoadingOverlay = ({ 
  isVisible = false, 
  type = 'login',
  customMessages = null 
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  
  // Get messages based on type
  const messages = customMessages || LOADING_MESSAGES[type] || LOADING_MESSAGES.login;

  // Cycle through messages every 2 seconds
  useEffect(() => {
    if (!isVisible) {
      setCurrentMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => {
        // Loop back to first message if we reach the end
        if (prev >= messages.length - 1) {
          return 0;
        }
        return prev + 1;
      });
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(interval);
  }, [isVisible, messages.length]);

  if (!isVisible) return null;

  return (
    <div className="loading-overlay">
      <div className="loading-overlay__content">
        {/* Pulsing spinner animation */}
        <div className="loading-overlay__spinner">
          <div className="loading-overlay__pulse"></div>
          <div className="loading-overlay__pulse loading-overlay__pulse--delayed"></div>
        </div>

        {/* Status message with fade transition */}
        <p 
          className="loading-overlay__message"
          key={currentMessageIndex}
        >
          {messages[currentMessageIndex]}
        </p>

        {/* Progress dots */}
        <div className="loading-overlay__dots">
          {messages.map((_, index) => (
            <span
              key={index}
              className={`loading-overlay__dot ${
                index === currentMessageIndex ? 'loading-overlay__dot--active' : ''
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadingOverlay;






